// src/services/customerService.js
import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

export const customerService = {
  
  /** 🔹 Get All Customers */
  getAll: async (filters = {}) => {
    try {
      const response = await apiClient.get(ENDPOINTS.CUSTOMER.GET_ALL, { params: filters });
      return response;
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      throw error;
    }
  },

  /** 🔹 Get Customer by ID */
  getById: async (id) => {
    try {
      const response = await apiClient.get(ENDPOINTS.CUSTOMER.GET_BY_ID(id));
      return response;
    } catch (error) {
      console.error(`Failed to fetch customer ${id}:`, error);
      throw error;
    }
  },

  /** 🔹 Create New Customer */
  create: async (data) => {
    try {
      const response = await apiClient.post(ENDPOINTS.CUSTOMER.CREATE, data);
      return response;
    } catch (error) {
      console.error('Failed to create customer:', error);
      throw error;
    }
  },

  /** 🔹 Update Existing Customer */
  update: async (id, data) => {
    try {
      const response = await apiClient.put(ENDPOINTS.CUSTOMER.UPDATE(id), data);
      return response;
    } catch (error) {
      console.error(`Failed to update customer ${id}:`, error);
      throw error;
    }
  },

  /** 🔹 Delete Customer */
  delete: async (id) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.CUSTOMER.DELETE(id));
      return response;
    } catch (error) {
      console.error(`Failed to delete customer ${id}:`, error);
      throw error;
    }
  },

  /** 🔹 Get Customer Profile (Purchase Summary & Recent Orders) */
  getCustomerProfile: async (id) => {
    try {
      const response = await apiClient.get(ENDPOINTS.CUSTOMER.GET_PROFILE(id));
      return response;
    } catch (error) {
      console.error(`Failed to fetch customer profile ${id}:`, error);
      throw error;
    }
  },

  /** 🔹 Get Customer Loyalty Transactions */
  getCustomerLoyaltyTransactions: async (id) => {
    try {
      const response = await apiClient.get(
        ENDPOINTS.CUSTOMER.GET_LOYALTY_TRANSACTIONS(id)
      );
      return response;
    } catch (error) {
      console.error(`Failed to fetch loyalty transactions for customer ${id}:`, error);
      throw error;
    }
  },

  /** 🔹 Get Campaign Customers */
  getCampaignCustomers: async ({ offset = 0, limit = 50, branchId } = {}) => {
    try {
      const params = { offset, limit };
      if (branchId) params.branchId = branchId;
      const response = await apiClient.get(
        ENDPOINTS.CUSTOMER.GET_CAMPAIGN_REPORT,
        { params }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch campaign customers:', error);
      throw error;
    }
  },

  /** 🔹 Get Customer Segment Counts */
  getCustomerSegmentCounts: async ({ branchId } = {}) => {
    const params = {};
    if (branchId) params.branchId = branchId;
    return apiClient.get(
      ENDPOINTS.CUSTOMER.GET_SEGMENT_COUNTS,
      { params }
    );
  },

  /** 🔹 Send Campaign to Customers */
  sendCampaign: async (data) => {
    try {
      const response = await apiClient.post(
        ENDPOINTS.CUSTOMER.SEND,
        data
      );
      return response;
    } catch (error) {
      console.error('Failed to send campaign:', error);
      throw error;
    }
  },

  /** 🔹 Get Loyalty Balance for a Customer */
  getLoyaltyBalance: async (customerId) => {
    try {
      return await apiClient.get(ENDPOINTS.CUSTOMER.GET_LOYALTY_BALANCE(customerId));
    } catch (error) {
      console.error('Failed to get loyalty balance:', error);
      return null;
    }
  },

  /** 🔹 Redeem Loyalty Points */
  redeemPoints: async (customerId, data) => {
    try {
      return await apiClient.post(ENDPOINTS.CUSTOMER.REDEEM_POINTS(customerId), data);
    } catch (error) {
      console.error('Failed to redeem points:', error);
      throw error;
    }
  },

  /** 🔹 Submit Customer Feedback (public — no auth required)
   *  POST /api/feedback
   */
  submitFeedback: async (branchId, data) => {
  try {
    const response = await apiClient.post(
      ENDPOINTS.CUSTOMER.SUBMIT_FEEDBACK(branchId),
      data
    );
    return response;
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    throw error;
  }
},

  /** 🔹 Get Feedback List (authenticated — backoffice)
   *  GET /api/admin/customer/feedback
   *
   *  @param {Object} params
   *  @param {string}  [params.fromDate]           - e.g. "2026-01-01"
   *  @param {string}  [params.toDate]             - e.g. "2026-05-04"
   *  @param {number}  [params.rating]             - exact star rating (1–5)
   *  @param {boolean} [params.resolved]           - filter by resolved status
   *  @param {string}  [params.search]             - name / phone / text search
   *  @param {number}  [params.page=0]             - zero-based page index
   *  @param {number}  [params.size=20]            - page size
   *  @param {string}  [params.sortBy='created_at']
   *  @param {string}  [params.sortDir='desc']
   */
  getFeedbackList: async (params = {}) => {
    try {
      const {
        fromDate,
        toDate,
        rating,
        resolved,
        search,
        page    = 0,
        size    = 20,
        sortBy  = 'created_at',
        sortDir = 'desc',
      } = params;

      const queryParams = {
        page,
        size,
        sortBy,
        sortDir,
        ...(fromDate          && { fromDate }),
        ...(toDate            && { toDate }),
        ...(rating   != null  && { rating }),
        ...(resolved != null  && { resolved }),
        ...(search            && { search }),
      };

      const response = await apiClient.get(
        ENDPOINTS.CUSTOMER.GET_FEEDBACK_LIST,
        { params: queryParams }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch feedback list:', error);
      throw error;
    }
  },

};

export default customerService;